// Vuex Store Module
// Place this file in: store/modules/barcodeStoreVuex.js (or stores/modules/barcodeStoreVuex.js)
// Import as BarcodeStoreVuex in your store/index.js and add to modules

import axios from 'axios';

// For Vite projects, use import.meta.env.VITE_API_BASE_URL
// For Vue CLI projects, use process.env.VUE_APP_API_BASE_URL
const getApiBaseUrl = () => {
  // Vue CLI process.env.VUE_APP_API_SERVER (check this first for Vue CLI projects)
  if (process.env.VUE_APP_API_SERVER) {
    return process.env.VUE_APP_API_SERVER;
  }
  // Vite
  if (
    typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_API_BASE_URL
  ) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  // Default
  return "http://127.0.0.1:8000/api";
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json"
  },
});

// Export as default - import in store/index.js as: import BarcodeStoreVuex from './modules/barcodeStoreVuex'
export default {
  namespaced: true,
  state: {
    batches: [],
    currentBatch: null,
    items: [],
    loading: false,
    error: null,
    productSearchResults: [],
    productSearchLoading: false,
    scanDetailRows: [],
    scanDetailMeta: {
      page: 1,
      perPage: 25,
      total: null,
      totalPages: null,
    },
  },

  mutations: {
    SET_BATCHES(state, batches) {
      state.batches = batches;
    },
    SET_CURRENT_BATCH(state, batch) {
      state.currentBatch = batch;
      state.items = batch?.items || [];
    },
    SET_ITEMS(state, items) {
      state.items = items;
    },
    UPDATE_ITEM(state, updatedItem) {
      const index = state.items.findIndex(item => item.id === updatedItem.id);
      if (index !== -1) {
        state.items[index] = updatedItem;
      }
    },
    REMOVE_ITEM(state, itemId) {
      state.items = state.items.filter(item => item.id !== itemId);
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_CURRENT_BATCH(state) {
      state.currentBatch = null;
      state.items = [];
    },
    SET_PRODUCT_SEARCH_RESULTS(state, results) {
      state.productSearchResults = results;
    },
    SET_PRODUCT_SEARCH_LOADING(state, loading) {
      state.productSearchLoading = loading;
    },
    CLEAR_PRODUCT_SEARCH(state) {
      state.productSearchResults = [];
    },
    SET_SCAN_DETAIL_ROWS(state, rows) {
      state.scanDetailRows = rows;
    },
    SET_SCAN_DETAIL_META(state, meta) {
      state.scanDetailMeta = meta;
    },
  },

  actions: {
    // Create batch
    async createBatch({ commit, dispatch, rootGetters }, { name, vendor = 'Manual', labelDate = null }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const response = await api.post('/admin/batches', { name, vendor, label_date: labelDate }, {
          headers: {
            Authorization: 'Bearer ' + rootGetters['userAccess/accessToken']
          }
        });
        await dispatch('fetchBatches');
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.detail || 'Error creating batch');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Get batch
    async getBatch({ commit, rootGetters }, batchId) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const response = await api.get(`/admin/batches/${batchId}`, {
          headers: {
            Authorization: 'Bearer ' + rootGetters['userAccess/accessToken']
          }
        });
        commit('SET_CURRENT_BATCH', response.data);
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.detail || 'Error loading batch');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Get all batches
    async fetchBatches({ commit, rootGetters }, { page = 1, perPage = 25, filename = '', silent = false } = {}) {
      try {
        if (!silent) {
          commit('SET_LOADING', true);
        }
        commit('SET_ERROR', null);
        const response = await api.get('/admin/batches', {
          params: {
            page,
            per_page: perPage,
            ...(filename ? { filename } : {}),
          },
          headers: {
            Authorization: 'Bearer ' + rootGetters['userAccess/accessToken']
          }
        });
        const data = response.data || {};
        const rows = Array.isArray(data)
          ? data
          : data.data || data.rows || data.results || data.items || [];
        commit('SET_BATCHES', rows);
        return rows;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.detail || 'Error loading batches');
        throw error;
      } finally {
        if (!silent) {
          commit('SET_LOADING', false);
        }
      }
    },

    // Delete batch
    async deleteBatch({ commit, dispatch, state, rootGetters }, batchId) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        await api.delete(`/admin/batches/${batchId}`, {
          headers: {
            Authorization: 'Bearer ' + rootGetters['userAccess/accessToken']
          }
        });
        if (state.currentBatch?.id === batchId) {
          commit('CLEAR_CURRENT_BATCH');
        }
        await dispatch('fetchBatches');
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.detail || 'Error deleting batch');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Update batch
    async updateBatch({ commit, state, rootGetters }, { batchId, batchData, silent = false }) {
      try {
        if (!silent) {
          commit('SET_LOADING', true);
        }
        commit('SET_ERROR', null);
        const response = await api.put(`/admin/batches/${batchId}`, batchData, {
          headers: {
            Authorization: 'Bearer ' + rootGetters['userAccess/accessToken']
          }
        });
        if (state.currentBatch?.id === batchId) {
          commit('SET_CURRENT_BATCH', { ...state.currentBatch, ...batchData });
        }
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.detail || 'Error updating batch');
        throw error;
      } finally {
        if (!silent) {
          commit('SET_LOADING', false);
        }
      }
    },

    // Update item
    async updateItem({ commit, rootGetters }, { itemId, itemData }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const response = await api.put(`/admin/items/${itemId}`, itemData, {
          headers: {
            Authorization: 'Bearer ' + rootGetters['userAccess/accessToken']
          }
        });
        commit('UPDATE_ITEM', { id: itemId, ...itemData });
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.detail || 'Error updating item');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Delete item
    async deleteItem({ commit, rootGetters }, itemId) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        await api.delete(`/admin/items/${itemId}`, {
          headers: {
            Authorization: 'Bearer ' + rootGetters['userAccess/accessToken']
          }
        });
        commit('REMOVE_ITEM', itemId);
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.detail || 'Error deleting item');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Create boxes
    async createBoxes({ commit, dispatch, rootGetters }, { batchId, boxData }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const response = await api.post(`/admin/batches/${batchId}/boxes`, boxData, {
          headers: {
            Authorization: 'Bearer ' + rootGetters['userAccess/accessToken']
          }
        });
        await dispatch('getBatch', batchId); // Reload batch to get updated items
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.detail || 'Error adding boxes');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Generate labels
    async generateLabels({ commit, rootGetters }, { batchId, itemIds = null, scanStrs = null }) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const payload = {};
        if (batchId) {
          payload.batch_id = batchId;
        } else if (scanStrs && scanStrs.length > 0) {
          // Some backends still validate presence of batch_id even for reprints.
          payload.batch_id = null;
        }
        if (itemIds && itemIds.length > 0) {
          payload.item_ids = itemIds;
        }
        if (scanStrs && scanStrs.length > 0) {
          payload.scan_strs = scanStrs;
        }

        const response = await api.post('/admin/labels/generate', payload, {
          headers: {
            Authorization: 'Bearer ' + rootGetters['userAccess/accessToken']
          },
          responseType: 'blob',
        });
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.detail || 'Error generating labels');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Upload file
    async uploadFile({ commit, rootGetters }, file) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const formData = new FormData();
        formData.append('file', file);
        const response = await api.post('/admin/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: 'Bearer ' + rootGetters['userAccess/accessToken']
          },
        });
        return response.data;
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.detail || 'Error uploading file');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    // Download PDF helper
    downloadPDF(context, { blob, filename }) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },

    // Search products for autocomplete
    async searchProducts({ commit, rootGetters }, searchTerm) {
      if (!searchTerm || searchTerm.length < 2) {
        commit('SET_PRODUCT_SEARCH_RESULTS', []);
        return [];
      }

      try {
        commit('SET_PRODUCT_SEARCH_LOADING', true);
        const response = await api.get('/Products', {
          params: {
            page: 1,
            items_per_page: 5,
            orderCol: 'Description',
            orderDir: 'desc',
            ActiveType: 'Y',
            TopSearch: searchTerm
          },
          headers: {
            Authorization: 'Bearer ' + rootGetters['userAccess/accessToken']
          }
        });

        // Extract products from response (adjust based on actual API response structure)
        const products = response.data?.data || response.data?.products || response.data || [];
        commit('SET_PRODUCT_SEARCH_RESULTS', products);
        return products;
      } catch (error) {
        console.error('Product search error:', error);
        commit('SET_PRODUCT_SEARCH_RESULTS', []);
        return [];
      } finally {
        commit('SET_PRODUCT_SEARCH_LOADING', false);
      }
    },

    // Clear product search results
    clearProductSearch({ commit }) {
      commit('CLEAR_PRODUCT_SEARCH');
    },

    // Get scan detail history (current labels)
    async fetchScanDetail(
      { commit, rootGetters },
      { page = 1, perPage = 25, boxnum = '', bin = '', location = '', commitResults = true }
    ) {
      try {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);
        const response = await api.get('/admin/scanDetail', {
          params: {
            page,
            per_page: perPage,
            ...(boxnum ? { boxnum } : {}),
            ...(bin ? { bin } : {}),
            ...(location ? { location } : {}),
          },
          headers: {
            Authorization: 'Bearer ' + rootGetters['userAccess/accessToken']
          }
        });

        const data = response.data || {};
        const rows = Array.isArray(data)
          ? data
          : data.data || data.rows || data.results || data.items || [];

        const total = data.total ?? data.total_count ?? data.count ?? null;
        const currentPage = data.page ?? data.current_page ?? page;
        const perPageValue = data.per_page ?? data.perPage ?? perPage;
        const totalPages = data.total_pages ?? (total && perPageValue ? Math.ceil(total / perPageValue) : null);

        const meta = {
          page: currentPage,
          perPage: perPageValue,
          total,
          totalPages,
        };

        if (commitResults) {
          commit('SET_SCAN_DETAIL_ROWS', rows);
          commit('SET_SCAN_DETAIL_META', meta);
        }

        return { rows, meta };
      } catch (error) {
        commit('SET_ERROR', error.response?.data?.detail || 'Error loading scan detail');
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },
  },
};
